import React from 'react';
import { useLocation } from 'react-router';

import { tr } from '../../i18n/text';
import { splitLocale } from '../../i18n/siteLocales';
import { trackCtaClick, trackEvent } from '../../services/analytics';

/**
 * Floating WhatsApp chat button, only where people actually prefer WhatsApp to a form:
 * visitors reading the Arabic or Hindi site, or whose browser time zone is in the Gulf or India.
 * Time zone (not an IP lookup) so nothing leaves the browser to decide this.
 */

const WHATSAPP_NUMBER = '918459691471';
const WHATSAPP_ZONES = /^Asia\/(Dubai|Riyadh|Qatar|Kuwait|Bahrain|Muscat|Kolkata|Calcutta)$/;

function prefersWhatsApp(pathname: string): boolean {
  const { locale } = splitLocale(pathname);
  if (locale === 'ar' || locale === 'hi') return true;
  try {
    return WHATSAPP_ZONES.test(Intl.DateTimeFormat().resolvedOptions().timeZone || '');
  } catch {
    return false;
  }
}

const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.07 2.88 1.21 3.08.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.69.25-1.29.17-1.41-.07-.12-.27-.2-.57-.35M12.05 21.79h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.86 9.86 0 0 1-1.51-5.26c0-5.45 4.44-9.88 9.89-9.88a9.82 9.82 0 0 1 6.99 2.9 9.82 9.82 0 0 1 2.89 6.99c0 5.45-4.44 9.88-9.88 9.88m8.41-18.29A11.81 11.81 0 0 0 12.05 0C5.5 0 .16 5.34.16 11.89c0 2.1.55 4.14 1.59 5.95L.06 24l6.31-1.65a11.88 11.88 0 0 0 5.68 1.45h.01c6.55 0 11.89-5.34 11.89-11.89 0-3.18-1.24-6.17-3.49-8.41" />
  </svg>
);

const WhatsAppChatButton: React.FC = () => {
  const { pathname } = useLocation();
  // Decided after mount: the prerendered HTML is built in New York, so it must not bake this in.
  const [show, setShow] = React.useState(false);

  React.useEffect(() => {
    setShow(prefersWhatsApp(pathname) && !/^\/(login|register|dashboard)/.test(pathname));
  }, [pathname]);

  if (!show) return null;

  const label = tr('Chat with us on WhatsApp');
  const href = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(tr('Hi Trainzilla, I have a question about the app.'))}`;
  const { locale } = splitLocale(pathname);

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener"
      aria-label={label}
      title={label}
      onClick={() => {
        trackCtaClick('whatsapp_chat', { page: pathname, locale });
        // Not a GA/Meta "Lead": a click is only intent. Mark whatsapp_click as a key event in GA4 instead.
        trackEvent('whatsapp_click', { page: pathname, locale });
      }}
      className="fixed z-50 bottom-24 md:bottom-6 end-4 md:end-6 w-14 h-14 rounded-full flex items-center justify-center text-white shadow-lg transition-transform hover:scale-105"
      style={{ backgroundColor: '#25D366' }}
    >
      <WhatsAppIcon className="w-7 h-7" />
    </a>
  );
};

export default WhatsAppChatButton;
